import { EntityService } from 'src/service/EntityService';

export type EmploymentContractField = {
  fieldName: string;
  placeholderText: string;
  fieldGroup: string;
  fieldLabel: string;
  fieldTypeEnumId?: string;
  isRequired?: string;
  defaultValue?: string | null;
  formatPattern?: string | null;
  sequenceNum?: number;
};

export type EmploymentContractTemplate = {
  templateCode: string;
  templateName: string;
  statusId?: string;
  agreementConfigureEnumId?: string;
};

export type EmploymentContractAgreementRow = {
  agreementId: string;
  agreementName?: string;
  agreementNo?: string;
  statusId?: string;
  statusDescription?: string;
  partyCount?: number | string;
  [key: string]: any;
};

export type EmploymentContractAgreement = {
  agreementId: string;
  agreementName?: string;
  agreementNo?: string;
  statusId?: string;
  [key: string]: any;
};

export type EmploymentContractAgreementParty = {
  partyId?: string;
  partyName?: string;
  emailAddress?: string;
  contactNumber?: string;
  roleTypeId?: string;
  sequenceNum?: number;
  [key: string]: any;
};

export type EmploymentContractSigningReasonCode =
  | 'SESSION_EXPIRED'
  | 'AGREEMENT_NOT_ELIGIBLE'
  | 'NOT_EMPLOYER_SIGNING_TURN'
  | 'SIGNING_IN_PROGRESS'
  | 'HSM_NOT_CONFIGURED'
  | string;

export type EmploymentContractSigningOption = {
  enabled: boolean;
  reasonCode?: EmploymentContractSigningReasonCode | null;
  message?: string | null;
  requiresCredentials?: boolean;
};

export type EmploymentContractSigningOptions = {
  usbToken: EmploymentContractSigningOption;
  hsm: EmploymentContractSigningOption;
};

export type EmploymentContractAgreementDetail = {
  success?: string;
  error?: string;
  agreement?: EmploymentContractAgreement;
  templateCode?: string;
  data?: Record<string, any>;
  agreementParties?: EmploymentContractAgreementParty[];
  signaturePositions?: any[];
  documentVersion?: string;
  signingOptions?: EmploymentContractSigningOptions;
  [key: string]: any;
};

export class EmploymentContractService extends EntityService {
  static entity = 'iam-employment-contract';

  static getTemplates(): Promise<{ templates: EmploymentContractTemplate[] }> {
    return this.axios.get(`${this.entity}/templates`).then(res => res.data);
  }

  static getTemplateMetadata(templateCode: string): Promise<{
    templateCode: string;
    templateName: string;
    agreementConfigureEnumId?: string;
    fields: EmploymentContractField[];
  }> {
    return this.axios.get(`${this.entity}/templates/${templateCode}/metadata`).then(res => res.data);
  }

  static getFormTemplate(templateCode: string): Promise<{
    templateCode: string;
    templateName: string;
    formHtml: string;
    success?: string;
    error?: string;
  }> {
    return this.axios.get(`${this.entity}/templates/${templateCode}/form`).then(res => res.data);
  }

  static previewTemplate(templateCode: string, data: any): Promise<{
    templateCode: string;
    pdfFileName: string;
    pdfDataBase64: string;
    fieldErrors?: Array<{ fieldName: string; message: string }>;
    success?: string;
    error?: string;
  }> {
    return this.axios.post(`${this.entity}/templates/${templateCode}/preview`, data, { skipLoading: true } as any).then(res => res.data);
  }

  static generateAgreement(templateCode: string, data: any): Promise<any> {
    return this.axios.post(`${this.entity}/templates/${templateCode}/generate-agreement`, data).then(res => res.data);
  }

  static getAgreement(agreementId: string, editableOnly = true): Promise<EmploymentContractAgreementDetail> {
    return this.axios.get(`${this.entity}/agreements/${agreementId}`, {
      params: { editableOnly: editableOnly ? 'Y' : 'N' }
    }).then(res => res.data);
  }

  static getDraftAgreement(agreementId: string): Promise<any> {
    return this.getAgreement(agreementId, true);
  }

  static updateDraftAgreement(agreementId: string, templateCode: string, data: any): Promise<any> {
    return this.axios.put(`${this.entity}/agreements/${agreementId}`, {
      ...data,
      templateCode
    }).then(res => res.data);
  }

  static saveIncompleteDraft(agreementId: string | undefined, templateCode: string, data: any): Promise<any> {
    const payload = {
      ...data,
      templateCode,
      draftOnly: true
    };
    return agreementId
      ? this.axios.put(`${this.entity}/agreements/${agreementId}`, payload).then(res => res.data)
      : this.axios.post(`${this.entity}/templates/${templateCode}/generate-agreement`, payload).then(res => res.data);
  }
}
